// =======================================================================
// ARQUIVO: calculateInventory.ts (VERSÃO CONFORME LEGISLAÇÃO)
// Lógica implementada conforme Código Civil (Lei 10.406/2002) e EC 132/2023:
// 1. Bens doados são EXCLUÍDOS do cálculo do patrimônio bruto existente.
// 2. O valor dos bens doados a herdeiros necessários é somado à herança
//    líquida para formar o "monte partível teórico" (Colação - Art. 2.003 CC).
// 3. A quota de cada herdeiro é calculada sobre este monte teórico.
// 4. O valor já recebido em doação é deduzido da parte final do herdeiro donatário.
// 5. A concorrência do cônjuge com descendentes no regime de comunhão parcial
//    é tratada corretamente, incidindo apenas sobre os bens particulares (Art. 1.829 CC).
// 6. Quota mínima do cônjuge quando for ascendente: 1/4 (25%) - Art. 1.832 CC.
// 7. Quota do cônjuge com ascendentes: 1/3 se grau 1, 1/2 se grau maior - Art. 1.837 CC.
// 8. ITCMD progressivo conforme EC 132/2023 (alíquotas variam por faixa de valor).
// =======================================================================

import {
    Inventario,
    PessoaInventario,
    CalculosInventario,
    PartilhaResultado,
    AnyAsset,
    ViolacaoOuAlertaLegislacao,
    Doacao,
    DoacaoPessoaInventario,
} from '../types';
import { SuccessoryProps } from './heirClassifier';
import { buildEmolumentosNotasFromCatalog } from './stateTablesCatalog';
import { getAnyAssetValueForPurpose } from './patrimonioAssetValue';
import { getItcmdSettings, getUfespRef } from './stateTablesCatalog';
import { getPrenotacaoValor, lookupRiRegistro } from './stateTablesCatalog';
import { getTableSetSummary } from './stateTablesCatalog';

// =======================================================================
// CONSTANTES
// =======================================================================

// Tabela progressiva de ITCMD para São Paulo (exemplo - deve ser configurável por estado)
// Conforme EC 132/2023, a alíquota pode ser progressiva; spec e sistema de referência usam 4% fixo
const ITCMD_FAIXAS_SP = [
    { limite: 0, aliquota: 0.00 },           // Isento até valor mínimo
    { limite: 50000, aliquota: 0.01 },       // 1% até R$ 50.000
    { limite: 200000, aliquota: 0.02 },      // 2% até R$ 200.000
    { limite: 500000, aliquota: 0.04 },      // 4% até R$ 500.000
    { limite: Infinity, aliquota: 0.06 },   // 6% acima de R$ 500.000
];

/** Usar alíquota fixa 4% por padrão (alinhado ao sistema de referência); false = progressivo */
const ITCMD_USAR_ALIQUOTA_FIXA = true;
const TERMS_VERSION = '2026-05-04-v1';

/**
 * Calcula a alíquota progressiva de ITCMD conforme EC 132/2023
 * @param valorQuinhao - Valor do quinhão hereditário ou doação
 * @returns Alíquota progressiva aplicável
 */
function calcularAliquotaITCMDProgressiva(valorQuinhao: number): number {
    if (valorQuinhao <= 0) return 0;
    
    // Encontra a faixa aplicável
    for (let i = ITCMD_FAIXAS_SP.length - 1; i >= 0; i--) {
        if (valorQuinhao > ITCMD_FAIXAS_SP[i].limite) {
            return ITCMD_FAIXAS_SP[i].aliquota;
        }
    }
    
    return ITCMD_FAIXAS_SP[0].aliquota;
}

/**
 * Calcula o valor do ITCMD progressivo sobre o quinhão
 * Imposto progressivo: cada faixa paga apenas sobre a parte que excede a faixa anterior
 * @param valorQuinhao - Valor do quinhão hereditário ou doação
 * @returns Valor do imposto devido
 */
function calcularITCMDProgressivo(valorQuinhao: number): number {
    if (valorQuinhao <= 0) return 0;
    
    let impostoTotal = 0;
    
    // Percorre as faixas de forma progressiva (da menor para a maior)
    for (let i = 0; i < ITCMD_FAIXAS_SP.length - 1; i++) {
        const faixaAtual = ITCMD_FAIXAS_SP[i];
        const faixaProxima = ITCMD_FAIXAS_SP[i + 1];
        
        // Se o valor está acima do limite da faixa atual
        if (valorQuinhao > faixaAtual.limite) {
            // Calcula o valor tributável nesta faixa
            const baseFaixa = Math.min(
                valorQuinhao - faixaAtual.limite,
                faixaProxima.limite - faixaAtual.limite
            );
            
            // Aplica a alíquota da faixa próxima (que é a aplicável a este intervalo)
            impostoTotal += baseFaixa * faixaProxima.aliquota;
        }
    }
    
    return impostoTotal;
}

const REGIME_COMUNHAO_UNIVERSAL = 'Comunhão Universal de Bens';
const REGIME_COMUNHAO_PARCIAL = 'Comunhão Parcial de Bens';
const REGIME_SEPARACAO_OBRIGATORIA = 'Regime de Separação Obrigatória dos Bens';
const REGIME_SEPARACAO_TOTAL = 'Separação Total de Bens';
const REGIME_PARTICIPACAO_FINAL_AQUESTOS = 'Participação Final nos Aquestos';

/** Linha de liberalidade para agregação ITCMD doação (SP): mesmo donatário + mesmo ano-civil. */
interface DoacaoLinhaItcmd {
    doacao: Doacao;
    dest: DoacaoPessoaInventario;
    valorConferido: number;
    donatedAssetId: number;
    assetDescription: string;
}

interface GrupoDoacaoItcmd {
    doneeId: string;
    anoCivilLabel: string;
    somaValorConferido: number;
    linhas: DoacaoLinhaItcmd[];
}

// Mapeamento de tipos de bens para nomes legíveis
const ASSET_CATEGORY_LABELS: Record<string, string> = {
    'imoveis': 'Imóvel',
    'veiculos': 'Veículo',
    'investimentos': 'Investimento',
    'participacoes_societarias': 'Participação Societária',
    'creditos': 'Crédito',
    'criptomoedas': 'Criptoativo',
    'outros_bens': 'Outro Bem',
    'bens_internacionais': 'Bem Internacional',
    'seguros': 'Seguro',
    'dividas': 'Dívida',
    'doacoes': 'Doação',
    'legados': 'Legado',
  };

// =======================================================================
// FUNÇÕES AUXILIARES DE CÁLCULO
// =======================================================================

function unifyAssets(inventory: Inventario): AnyAsset[] {
    // Consolida todos os bens listados no inventário
    return [
        ...(inventory.imoveis || []), ...(inventory.veiculos || []),
        ...(inventory.investimentos || []), ...(inventory.participacoes_societarias || []),
        ...(inventory.creditos || []), ...(inventory.criptomoedas || []),
        ...(inventory.outros_bens || []), ...(inventory.seguros || []),
        ...(inventory.bens_internacionais || []),
    ];
}

function getAssetValueDataObito(asset: AnyAsset): number {
    return getAnyAssetValueForPurpose(asset, 'DATA_OBITO');
}

function getAssetValueDataEscritura(asset: AnyAsset): number {
    return getAnyAssetValueForPurpose(asset, 'DATA_ESCRITURA');
}

function imovelTemVenalAtualizadoDeclarado(asset: AnyAsset): boolean {
    if (!('tipo_imovel' in asset)) return false;
    const im = asset as { valor_historico?: unknown };
    const h = typeof im.valor_historico === 'number' ? im.valor_historico : parseFloat(String(im.valor_historico ?? 0)) || 0;
    return h > 0.01;
}

/**
 * Cria um mapa de ID do bem para sua categoria (chave do inventário).
 */
function buildAssetCategoryMap(inventory: Inventario): Map<number, string> {
    const categoryMap = new Map<number, string>();
    if (!inventory) return categoryMap;
    
    const assetKeys = ['imoveis', 'veiculos', 'investimentos', 'participacoes_societarias', 
                       'creditos', 'criptomoedas', 'outros_bens', 'bens_internacionais', 'seguros'];
    
    assetKeys.forEach(key => {
        ((inventory as any)[key] || []).forEach((asset: AnyAsset) => {
            if (asset?.id != null) {
                categoryMap.set(Number(asset.id), key);
            }
        });
    });
    
    return categoryMap;
}

/**
 * Determina se um bem é comum (da comunhão) ou particular com base no regime de bens
 * e indicação explícita no cadastro (e_bem_particular).
 */
function isDeCujusAssetCommon(asset: AnyAsset, decujus: PessoaInventario): 'comum' | 'particular_decujus' {
    // Indicação explícita no cadastro: bem particular não entra na meação
    if ((asset as AnyAsset & { e_bem_particular?: boolean }).e_bem_particular === true) {
        return 'particular_decujus';
    }

    const { casamento_regime: regime, casamento_data } = decujus;

    if (regime === REGIME_COMUNHAO_UNIVERSAL) return 'comum';
    if (regime === REGIME_SEPARACAO_TOTAL || regime === REGIME_SEPARACAO_OBRIGATORIA) return 'particular_decujus';

    if (regime === REGIME_COMUNHAO_PARCIAL) {
        // Campo legado data_aquisicao é ignorado. Sem marcação explícita de bem particular, presumir comum.
        return 'comum';
    }

    return 'particular_decujus';
}

/**
 * Verifica se o cônjuge é ascendente (pai/mãe) dos descendentes herdeiros
 */
function isConjugeAscendenteDosHerdeiros(
    conjugeId: string,
    descendentes: SuccessoryProps[],
    familyMap: Map<string, PessoaInventario>
): boolean {
    return descendentes.some(desc => {
        const descPerson = familyMap.get(desc.id?.toString() || '');
        return descPerson?.parentIds?.includes(conjugeId);
    });
}

/**
 * Determina os herdeiros chamados à sucessão e suas quotas percentuais.
 */
function determineHeirsAndSharesFromClassifier(
    decujusId: string,
    familyMap: Map<string, PessoaInventario>,
    successoryDataMap: Map<string, SuccessoryProps>
): { shares: PartilhaResultado[], observacoes: string[], winningClass: number } {
    if (!decujusId || successoryDataMap.size === 0) {
        return { shares: [], observacoes: ['Dados insuficientes para determinar herdeiros.'], winningClass: 0 };
    }
    
    const allHeirProps = Array.from(successoryDataMap.values()).filter(p => p && p.id);
    const livingHeirs = allHeirProps.filter(p => familyMap.get(p.id?.toString() || '')?.esta_vivo && p.ehHerdeiroLegitimo);

    if (livingHeirs.length === 0) {
        return { shares: [], observacoes: ["Nenhum herdeiro legítimo vivo encontrado."], winningClass: 0 };
    }

    const winningClass = Math.min(...livingHeirs.map(p => p.classeSucessoria).filter(c => c > 0 && c < 5));

    if (!isFinite(winningClass)) {
        return { shares: [{ herdeiroId: "-99", herdeiroNome: 'Herança Jacente/Vacante (Poder Público)', valorRecebidoBruto: 0, itcmdDevido: 0, valorRecebidoLiquido: 0, valorHerancaLiquida: 1 }], observacoes: ["Nenhum herdeiro legítimo encontrado."], winningClass: 0 };
    }

    const heirsInWinningClass = livingHeirs.filter(p => p.classeSucessoria === winningClass);
    let shares: PartilhaResultado[] = [];
    let observacoes: string[] = [`Classe ${winningClass} foi chamada à sucessão.`];

    switch (winningClass) {
        case 1: // Descendentes (com ou sem cônjuge) — Art. 1.833 e 1.852 CC: mais próximos excluem remotos, salvo direito de representação na linha descendente
            const conjuge = heirsInWinningClass.find(p => p.vinculoDC === 'Cônjuge / Companheiro');
            const descendentes = heirsInWinningClass.filter(p => p.vinculoDC === 'Descendente');
            
            const minGrau = Math.min(...descendentes.map(d => d.grauParentesco));
            const headsOfLineage = descendentes.filter(d => d.grauParentesco === minGrau);
            
            let quotaConjuge = 0;
            let quotaDescendentes = 0;
            
            if (conjuge) {
                // Art. 1.832 CC: Verifica se cônjuge é ascendente dos herdeiros
                const conjugeEhAscendente = isConjugeAscendenteDosHerdeiros(
                    conjuge.id?.toString() || '',
                    headsOfLineage,
                    familyMap
                );
                
                const totalHeads = headsOfLineage.length + 1;
                const sharePerHead = 1 / totalHeads;
                
                // Art. 1.832 CC: Se for ascendente, quota mínima é 1/4 (25%)
                if (conjugeEhAscendente && sharePerHead < 0.25) {
                    quotaConjuge = 0.25;
                    // Recalcula quotas dos descendentes proporcionalmente
                    const quotaRestante = 1 - quotaConjuge;
                    quotaDescendentes = headsOfLineage.length > 0 ? quotaRestante / headsOfLineage.length : 0;
                    observacoes.push(`Cônjuge é ascendente dos herdeiros - quota mínima de 25% aplicada (Art. 1.832 CC).`);
                } else {
                    quotaConjuge = sharePerHead;
                    quotaDescendentes = sharePerHead;
                }
                
                shares.push({ herdeiroId: conjuge.id, herdeiroNome: '', valorHerancaLiquida: quotaConjuge, valorRecebidoBruto: 0, itcmdDevido: 0, valorRecebidoLiquido: 0 });
                observacoes.push(`Cônjuge concorre com descendentes, partilha por cabeça.`);
            } else {
                quotaDescendentes = headsOfLineage.length > 0 ? 1 / headsOfLineage.length : 0;
            }

            headsOfLineage.forEach(child => {
                if (!child?.id) return;
                const childPerson = familyMap.get(child.id.toString());
                if (!childPerson) return;
                
                if (childPerson.esta_vivo) {
                    shares.push({ herdeiroId: child.id, herdeiroNome: '', valorHerancaLiquida: quotaDescendentes, valorRecebidoBruto: 0, itcmdDevido: 0, valorRecebidoLiquido: 0 });
                } else {
                    const representatives = descendentes.filter(rep => rep?.id && rep.grauParentesco > minGrau && familyMap.get(rep.id.toString())?.parentIds?.includes(child.id.toString()));
                    if (representatives.length > 0) {
                        const sharePerRepresentative = quotaDescendentes / representatives.length;
                        observacoes.push(`A estirpe de ${childPerson.nome || 'herdeiro'} (pré-morto) é partilhada entre ${representatives.length} representante(s) (Art. 1.852 CC).`);
                        representatives.forEach(rep => {
                            shares.push({ herdeiroId: rep.id, herdeiroNome: '', valorHerancaLiquida: sharePerRepresentative, valorRecebidoBruto: 0, itcmdDevido: 0, valorRecebidoLiquido: 0 });
                        });
                    }
                    shares.push({ herdeiroId: child.id, herdeiroNome: '', valorHerancaLiquida: 0, valorRecebidoBruto: 0, itcmdDevido: 0, valorRecebidoLiquido: 0 });
                }
            });
            break;

        case 2: // Ascendentes — Art. 1.852 CC: direito de representação nunca na linha ascendente; só os ascendentes vivos no grau mais próximo herdam
            const conjugeAsc = heirsInWinningClass.find(p => p.vinculoDC === 'Cônjuge / Companheiro');
            const ascendentes = heirsInWinningClass.filter(p => p.vinculoDC === 'Ascendente');
            const grauMinimoAsc = Math.min(...ascendentes.map(a => a.grauParentesco));
            const ascendentesMaisProximos = ascendentes.filter(a => a.grauParentesco === grauMinimoAsc);

            if (conjugeAsc) {
                // Art. 1.837 CC: Concorrendo com ascendente em primeiro grau, ao cônjuge tocará um terço da herança;
                // caber-lhe-á a metade desta se houver um só ascendente, ou se maior for aquele grau.
                let quotaConjuge: number;
                if (grauMinimoAsc === 1) {
                    // Ascendente em 1º grau (pai/mãe): cônjuge = 1/3
                    quotaConjuge = 1 / 3;
                    observacoes.push(`Cônjuge concorre com ascendente em 1º grau - quota de 1/3 aplicada (Art. 1.837 CC).`);
                } else {
                    // Grau maior OU apenas 1 ascendente: cônjuge = 1/2
                    quotaConjuge = 1 / 2;
                    if (ascendentesMaisProximos.length === 1) {
                        observacoes.push(`Cônjuge concorre com apenas um ascendente - quota de 1/2 aplicada (Art. 1.837 CC).`);
                    } else {
                        observacoes.push(`Cônjuge concorre com ascendente de grau maior que 1 - quota de 1/2 aplicada (Art. 1.837 CC).`);
                    }
                }
                
                const quotaAscendentesTotal = 1 - quotaConjuge;
                const quotaIndividualAscendente = ascendentesMaisProximos.length > 0 ? quotaAscendentesTotal / ascendentesMaisProximos.length : 0;
                shares.push({ herdeiroId: conjugeAsc.id, herdeiroNome: '', valorHerancaLiquida: quotaConjuge, valorRecebidoBruto: 0, itcmdDevido: 0, valorRecebidoLiquido: 0 });
                ascendentesMaisProximos.forEach(asc => shares.push({ herdeiroId: asc.id, herdeiroNome: '', valorHerancaLiquida: quotaIndividualAscendente, valorRecebidoBruto: 0, itcmdDevido: 0, valorRecebidoLiquido: 0 }));
            } else {
                const quotaIndividual = ascendentesMaisProximos.length > 0 ? 1 / ascendentesMaisProximos.length : 0;
                ascendentesMaisProximos.forEach(asc => shares.push({ herdeiroId: asc.id, herdeiroNome: '', valorHerancaLiquida: quotaIndividual, valorRecebidoBruto: 0, itcmdDevido: 0, valorRecebidoLiquido: 0 }));
            }
            break;

        case 3: // Apenas Cônjuge
            const conjugeUnico = heirsInWinningClass.find(p => p.classeSucessoria === 3)!;
            shares.push({ herdeiroId: conjugeUnico.id, herdeiroNome: '', valorHerancaLiquida: 1, valorRecebidoBruto: 0, itcmdDevido: 0, valorRecebidoLiquido: 0 });
            break;

        case 4: // Colaterais — Art. 1.840 e 1.853 CC: mais próximos excluem remotos; direito de representação só em favor dos filhos de irmãos quando com irmãos concorrerem
            const colaterais = heirsInWinningClass.filter(p => p.classeSucessoria === 4);
            const grauMinimoCol = Math.min(...colaterais.map(c => c.grauParentesco));
            // Cabeças de linha = todos os irmãos (grau 2), vivos e mortos, para repartir quota dos mortos entre representantes (Art. 1.853)
            const allSiblingProps = Array.from(successoryDataMap.entries())
                .filter(([, p]) => p.vinculoDC === 'Colateral' && p.grauParentesco === grauMinimoCol)
                .map(([id, p]) => ({ id, props: p }));
            let totalWeightCol = 0;
            allSiblingProps.forEach(({ props }) => { totalWeightCol += (props.isBilateral ? 2 : 1); });
            allSiblingProps.forEach(({ id, props: col }) => {
                const colPerson = familyMap.get(id);
                if (!colPerson) return;
                const weight = col.isBilateral ? 2 : 1;
                const quotaHead = totalWeightCol > 0 ? weight / totalWeightCol : 0;
                if (colPerson.esta_vivo) {
                    shares.push({ herdeiroId: id, herdeiroNome: '', valorHerancaLiquida: quotaHead, valorRecebidoBruto: 0, itcmdDevido: 0, valorRecebidoLiquido: 0 });
                } else {
                    const representativesCol = colaterais.filter(rep => rep?.id && rep.grauParentesco > grauMinimoCol && familyMap.get(rep.id.toString())?.parentIds?.includes(id));
                    if (representativesCol.length > 0) {
                        const sharePerRep = quotaHead / representativesCol.length;
                        observacoes.push(`A estirpe de ${colPerson.nome || 'irmão(ã)'} (pré-morto) é partilhada entre ${representativesCol.length} representante(s) (Art. 1.853 CC).`);
                        representativesCol.forEach(rep => {
                            shares.push({ herdeiroId: rep.id, herdeiroNome: '', valorHerancaLiquida: sharePerRep, valorRecebidoBruto: 0, itcmdDevido: 0, valorRecebidoLiquido: 0 });
                        });
                    }
                    shares.push({ herdeiroId: id, herdeiroNome: '', valorHerancaLiquida: 0, valorRecebidoBruto: 0, itcmdDevido: 0, valorRecebidoLiquido: 0 });
                }
            });
            break;
    }

    shares.forEach(s => {
        s.herdeiroNome = familyMap.get(s.herdeiroId?.toString() || '')?.nome || 'Desconhecido';
    });
    return { shares, observacoes, winningClass };
}

// =======================================================================
// LÓGICA DE CÁLCULO PRINCIPAL
// =======================================================================

export function calculateInventory(
    inventory: Inventario,
    successoryDataMap: Map<string, SuccessoryProps>,
): CalculosInventario {

    // Validação inicial - retorna cálculos vazios se dados essenciais estão ausentes
    if (!inventory || !inventory.decujus) {
        const base: CalculosInventario = {
            totalPatrimonioBruto: 0,
            totalPatrimonioLiquido: 0,
            valorDoacoesExcluidas: 0,
            totalDividas: 0,
            totalDividasCadastradas: 0,
            totalSaldoFinanciamentos: 0,
            financiamentosDetalhe: [],
            meacaoValor: 0,
            herancaValorBruto: 0,
            herancaValorLiquido: 0,
            itcmdAliquota: 0, // Alíquota será calculada quando houver valores
            partilhaPorHerdeiro: [],
            itcmdDevidoTotal: 0,
            observacoesCalculo: ['Dados do inventário incompletos para realizar os cálculos.'],
            legitimaValor: 0,
            disponivelValor: 0,
            monteColacionavel: 0,
            valorLegadoTotal: 0,
            valorLegadoReduzido: 0,
            violacoesOuAlertasLegislacao: [],
        };

        const emolCat = buildEmolumentosNotasFromCatalog(0, 'SP');
        base.emolumentosFaixa = emolCat.emolumentosFaixa;
        base.emolumentosFaixaValorInicial = emolCat.emolumentosFaixaValorInicial;
        base.emolumentosFaixaValorFinal = emolCat.emolumentosFaixaValorFinal;
        base.emolumentosTotalSemIss = emolCat.emolumentosTotalSemIss;
        base.emolumentosTabeliao = emolCat.emolumentosTabeliao;
        base.emolumentosIss = emolCat.emolumentosIss;
        base.emolumentosTotal = emolCat.emolumentosTotal;
        base.emolumentosPiorCasoTotal = emolCat.emolumentosPiorCasoTotal;
        base.emolumentosEconomiaVsPiorCaso = emolCat.emolumentosEconomiaVsPiorCaso;

        return base;
    }

    const { decujus, familiares = [], dividas = [], doacoes = [], legados = [], herdeiros_testamentarios = [] } = inventory;
    const allFamiliares = [decujus, ...(familiares || [])].filter(p => p && p.id != null);
    const familyMap = new Map<string, PessoaInventario>();
    allFamiliares.forEach(p => {
        const key = p.id?.toString();
        if (key) familyMap.set(key, p);
    });
    const allAssets = unifyAssets(inventory).filter(a => a && a.id != null);
    const assetCategoryMap = buildAssetCategoryMap(inventory);
    const stateCode = 'SP';
    const ufesp = getUfespRef(stateCode);
    const itcmdSettings = getItcmdSettings(stateCode);
    const itcmdAliquotaFixa = itcmdSettings.aliquotaPadrao ?? 0.04;
    const itcmdIsencaoDoacaoValor = (itcmdSettings.isencaoDoacaoUf ?? 0) * (ufesp.valueBrl ?? 0);
    const tableSetSummary = getTableSetSummary(stateCode);
    const simulationDate = new Date().toISOString().slice(0, 10);

    // Calcula a porcentagem do BEM que foi doada (para exclusão do patrimônio bruto).
    // Conforme Art. 2.003 CC e ANALISE_LEGISLACAO: apenas a parte NÃO doada permanece no patrimônio.
    // Usa percentual_nua_propriedade da doação (quanto do bem foi doado), NÃO porcentagem_doacao
    // (que é a quota da doação entre os donatários). Em doação com usufruto (SP), o cadastro usa
    // ⅔ (66,67%) como parcela da nua-propriedade no total da liberalidade, alinhado à tabela de ITCMD estadual.
    const donatedPercentageByAsset = new Map<number, number>();
    (doacoes || []).forEach(doacao => {
        if (!doacao?.bem_relacionado) return;
        const donatedAssetId = parseInt(doacao.bem_relacionado.split(':')[1], 10);
        if (!isNaN(donatedAssetId)) {
            const percentualDoadoDoBem = typeof doacao.percentual_nua_propriedade === 'number'
                ? doacao.percentual_nua_propriedade
                : parseFloat(String(doacao.percentual_nua_propriedade ?? 0)) || 0;
            const existing = donatedPercentageByAsset.get(donatedAssetId) ?? 0;
            donatedPercentageByAsset.set(donatedAssetId, Math.min(100, Math.max(existing, percentualDoadoDoBem)));
        }
    });

    let patrimonioComumBruto = 0;
    let patrimonioParticularBruto = 0;

    allAssets.forEach(asset => {
        const assetId = Number(asset.id);
        const donatedPercentage = donatedPercentageByAsset.get(assetId) || 0;
        const remainingPercentage = (100 - donatedPercentage) / 100;

        // Considera apenas a parte NÃO doada do bem
        const valorBem = getAssetValueDataObito(asset) * remainingPercentage;
        if (valorBem > 0) {
            if (isDeCujusAssetCommon(asset, decujus) === 'comum') patrimonioComumBruto += valorBem;
            else patrimonioParticularBruto += valorBem;
        }
    });

    const patrimonioBrutoDeCujus = patrimonioComumBruto + patrimonioParticularBruto;
    const regime = decujus.casamento_regime;
    const conjuge = allFamiliares.find(f => f?.id && successoryDataMap.get(f.id.toString())?.vinculoDC === 'Cônjuge / Companheiro');
    
    let meacaoValor = 0;
    const meacaoAssetShares: PartilhaResultado['assetShares'] = [];
    if (conjuge) {
        let patrimonioComumTotalParaMeacao = 0;
        allAssets.forEach(asset => {
            if (isDeCujusAssetCommon(asset, decujus) === 'comum') {
                const assetId = Number(asset.id);
                const donatedPercentage = donatedPercentageByAsset.get(assetId) || 0;
                const remainingPercentage = (100 - donatedPercentage) / 100;
                
                // Considera apenas a parte NÃO doada do bem para meação
                const valorBemComum = getAssetValueDataObito(asset) * remainingPercentage;
                patrimonioComumTotalParaMeacao += valorBemComum;
                if (valorBemComum > 0) {
                    const category = assetCategoryMap.get(Number(asset.id)) || 'outros_bens';
                    meacaoAssetShares.push({
                        assetId: asset.id,
                        assetDescription: asset.descricao || '',
                        value: valorBemComum / 2,
                        type: 'Meação',
                        category,
                        categoryLabel: ASSET_CATEGORY_LABELS[category] || 'Outro Bem'
                    });
                }
            }
        });
        
        if (regime === REGIME_COMUNHAO_UNIVERSAL || regime === REGIME_COMUNHAO_PARCIAL || regime === REGIME_PARTICIPACAO_FINAL_AQUESTOS) {
            meacaoValor = patrimonioComumTotalParaMeacao / 2;
        }
    }

    // Dívidas cadastradas (array dividas) + saldos de financiamento de bens com financiamento ativo (Art. 1.847 CC)
    const totalDividasCadastradas = dividas.reduce((sum, div) => sum + (div.saldo_devedor ?? 0), 0);
    const assetsWithFinancing = [
        ...(inventory.imoveis || []).map((a: any) => ({ ...a, _tipo: 'Imóvel' })),
        ...(inventory.veiculos || []).map((a: any) => ({ ...a, _tipo: 'Veículo' })),
        ...(inventory.outros_bens || []).map((a: any) => ({ ...a, _tipo: 'Outro bem' })),
        ...(inventory.bens_internacionais || []).map((a: any) => ({ ...a, _tipo: 'Bem internacional' })),
    ].filter((a: any) => a?.esta_financiado === true && (a?.saldo_devedor ?? 0) > 0);
    const totalSaldoFinanciamentos = assetsWithFinancing.reduce((sum, a) => sum + ((a as any).saldo_devedor ?? 0), 0);
    const financiamentosDetalhe = assetsWithFinancing.map((a: any) => ({
        descricao: a.descricao || `${a._tipo} #${a.id}`,
        saldo_devedor: a.saldo_devedor ?? 0,
        tipo: a._tipo || 'Bem',
    }));
    const totalDividas = totalDividasCadastradas + totalSaldoFinanciamentos;

    // Patrimônio bruto total (valor integral dos bens) e valor das doações excluídas
    const totalPatrimonioBrutoCalc = allAssets.reduce((sum, asset) => sum + getAssetValueDataObito(asset), 0);
    const valorDoacoesExcluidasCalc = (doacoes || []).reduce((sum, d) => sum + (d.valor ?? 0), 0);

    // Herança líquida conforme fórmula da tabela: Patrimônio Bruto − Doações − Dívidas − Meação
    const herancaBruta = patrimonioBrutoDeCujus - meacaoValor;
    const herancaValorLiquido = totalPatrimonioBrutoCalc - valorDoacoesExcluidasCalc - totalDividas - meacaoValor;

    const decujusId = decujus.id?.toString() || '';
    const { shares: quotas, observacoes: obsSucessao, winningClass } = decujusId 
        ? determineHeirsAndSharesFromClassifier(decujusId, familyMap, successoryDataMap)
        : { shares: [], observacoes: ['ID do de cujus não disponível.'], winningClass: 0 };
    
    const partilhasMap = new Map<string, PartilhaResultado>();
    const getOrCreatePartilha = (id: string, nome: string): PartilhaResultado => {
        if (!partilhasMap.has(id)) {
            partilhasMap.set(id, { herdeiroId: id, herdeiroNome: nome, valorMeacao: 0, valorHerancaLiquida: 0, valorRecebidoBruto: 0, itcmdDevido: 0, valorRecebidoLiquido: 0, assetShares: [], });
        }
        return partilhasMap.get(id)!;
    };

    if (conjuge && conjuge.id) {
        const partilhaConjuge = getOrCreatePartilha(conjuge.id.toString(), conjuge.nome || 'Cônjuge');
        partilhaConjuge.assetShares!.push(...meacaoAssetShares);
    }
    
    /** Valores conferidos só para herdeiros necessários (colação nos quotas teóricos — CC). */
    const colacaoPorHerdeiro = new Map<string, number>();
    /** ITCMD sobre liberalidades: agregação por (donatário, ano-civil); imposto rateado às linhas. */
    const itcmdDoacaoTaxPorPessoa = new Map<string, number>();
    /** Soma das parcelas conferidas a cada pessoa em todas as liberalidades (todas as categorias de destinatários). */
    const valorLiberalidadePorPessoa = new Map<string, number>();
    let totalCollationValue = 0;
    let alertaDoacaoSemDataItcmd = false;
    const assetMap = new Map<number, AnyAsset>(allAssets.map(a => [a.id, a]));

    const gruposDoacaoItcmd = new Map<string, GrupoDoacaoItcmd>();
    (doacoes || []).forEach(doacao => {
        if (!doacao?.bem_relacionado) return;
        const donatedAssetId = parseInt(doacao.bem_relacionado.split(':')[1], 10);
        const donatedAsset = assetMap.get(donatedAssetId);
        const assetDescription = donatedAsset?.descricao || 'Bem doado';
        const valorDoacaoCadastro = doacao.valor ?? 0;

        const anoCivilLabel = (() => {
            const raw = doacao.data_doacao ? new Date(doacao.data_doacao) : null;
            if (raw && !isNaN(raw.getTime())) return String(raw.getFullYear());
            alertaDoacaoSemDataItcmd = true;
            return 'sem-data';
        })();

        (doacao.doacao_pessoas_inventario || []).forEach(dest => {
            if (!dest?.pessoa_inventario_id) return;
            const doneeId = dest.pessoa_inventario_id.toString();
            if (!familyMap.get(doneeId)) return;

            const valorConferido = valorDoacaoCadastro * ((dest.porcentagem_doacao || 0) / 100);
            if (valorConferido <= 0) return;

            const key = `${doneeId}|${anoCivilLabel}`;
            let grupo = gruposDoacaoItcmd.get(key);
            if (!grupo) {
                grupo = { doneeId, anoCivilLabel, somaValorConferido: 0, linhas: [] };
                gruposDoacaoItcmd.set(key, grupo);
            }
            grupo.somaValorConferido += valorConferido;
            grupo.linhas.push({
                doacao,
                dest,
                valorConferido,
                donatedAssetId,
                assetDescription,
            });
        });
    });

    gruposDoacaoItcmd.forEach(grupo => {
        const excedeLimiteIsencao =
            itcmdIsencaoDoacaoValor <= 0 || grupo.somaValorConferido > itcmdIsencaoDoacaoValor;
        const baseTributavelGrupo = excedeLimiteIsencao ? grupo.somaValorConferido : 0;
        const impostoGrupo = ITCMD_USAR_ALIQUOTA_FIXA
            ? baseTributavelGrupo * itcmdAliquotaFixa
            : calcularITCMDProgressivo(baseTributavelGrupo);

        grupo.linhas.forEach(linha => {
            const doneeId = grupo.doneeId;
            const proporcao =
                grupo.somaValorConferido > 0 ? linha.valorConferido / grupo.somaValorConferido : 0;
            const impostoLinha = impostoGrupo * proporcao;
            itcmdDoacaoTaxPorPessoa.set(
                doneeId,
                (itcmdDoacaoTaxPorPessoa.get(doneeId) || 0) + impostoLinha,
            );
            valorLiberalidadePorPessoa.set(
                doneeId,
                (valorLiberalidadePorPessoa.get(doneeId) || 0) + linha.valorConferido,
            );

            if (successoryDataMap.get(doneeId)?.ehHerdeiroNecessario) {
                const donee = familyMap.get(doneeId)!;
                const donatedCategory = assetCategoryMap.get(linha.donatedAssetId) || 'doacoes';
                getOrCreatePartilha(doneeId, donee.nome || 'Donatário').assetShares!.push({
                    assetId: linha.donatedAssetId,
                    assetDescription: linha.assetDescription,
                    value: linha.valorConferido,
                    type: 'Doação',
                    category: donatedCategory,
                    categoryLabel: ASSET_CATEGORY_LABELS[donatedCategory] || 'Doação',
                });
                colacaoPorHerdeiro.set(doneeId, (colacaoPorHerdeiro.get(doneeId) || 0) + linha.valorConferido);
                totalCollationValue += linha.valorConferido;
            }
        });
    });

    // Quota testamentária como % da herança líquida (Art. 1.789 CC: parte disponível até 50%)
    const hasHerdeirosNecessarios = Array.from(successoryDataMap.values()).some(p => p && p.ehHerdeiroNecessario && familyMap.get(p.id?.toString() || '')?.esta_vivo);
    const quotaTotalTestamentariaPct = (herdeiros_testamentarios || []).reduce((sum: number, ht: any) => {
        const q = typeof ht.quota_testamentaria === 'number' ? ht.quota_testamentaria : parseFloat(String(ht.quota_testamentaria || 0)) || 0;
        return sum + Math.min(100, Math.max(0, q)) / 100;
    }, 0);
    const parteDisponivelMaxPct = 0.5;
    const valorTestamentarioTotal = hasHerdeirosNecessarios
        ? herancaValorLiquido * Math.min(quotaTotalTestamentariaPct, parteDisponivelMaxPct)
        : herancaValorLiquido * Math.min(quotaTotalTestamentariaPct, 1);
    const legitimaValor = hasHerdeirosNecessarios ? Math.max(0, herancaValorLiquido - valorTestamentarioTotal) : 0;
    // Art. 1.839/1.840 CC: quando só colaterais herdam, o valor a partilhar é a herança líquida menos a parte testamentária
    const valorPartilhaColaterais = (winningClass === 4 && herancaValorLiquido > 0) ? Math.max(0, herancaValorLiquido - valorTestamentarioTotal) : 0;
    
    const isComunhaoParcialConjugeDescendentes = regime === REGIME_COMUNHAO_PARCIAL
        && quotas.some(q => successoryDataMap.get(q?.herdeiroId?.toString() || '')?.vinculoDC === 'Cônjuge / Companheiro')
        && quotas.some(q => successoryDataMap.get(q?.herdeiroId?.toString() || '')?.vinculoDC === 'Descendente');
    const proporcaoComumNaHeranca = herancaBruta > 0 ? (patrimonioComumBruto / 2) / herancaBruta : 0;
    const legitimaFromComum = legitimaValor * proporcaoComumNaHeranca;
    const legitimaFromParticular = legitimaValor * (1 - proporcaoComumNaHeranca);
    const numDescendentesNaClasse = quotas.filter(q => successoryDataMap.get(q?.herdeiroId?.toString() || '')?.vinculoDC === 'Descendente').length;
    const totalCabeçasLegitima = quotas.length;
    if (isComunhaoParcialConjugeDescendentes && totalCabeçasLegitima > 0) {
        obsSucessao.push(`Cônjuge (regime comunhão parcial) herda apenas sobre a fração particular da legítima; descendentes recebem a fração comum e a parte particular por cabeça (Art. 1.829 CC).`);
    }

    quotas.forEach(quota => {
        if (!quota?.herdeiroId) return;
        const heirId = quota.herdeiroId.toString();
        const Q = quota.valorHerancaLiquida || 0;
        const doacao = colacaoPorHerdeiro.get(heirId) || 0;
        const heirProps = successoryDataMap.get(heirId);
        
        let theoreticalEntitlement = 0;
        if (winningClass === 4 && valorPartilhaColaterais > 0) {
            // Art. 1.839/1.840 CC: colaterais não são herdeiros necessários; partilham a herança líquida menos a parte testamentária (colaterais não colacionam)
            theoreticalEntitlement = valorPartilhaColaterais * Q;
        } else if (isComunhaoParcialConjugeDescendentes && numDescendentesNaClasse > 0 && totalCabeçasLegitima > 0) {
            if (heirProps?.vinculoDC === 'Cônjuge / Companheiro') {
                theoreticalEntitlement = legitimaFromParticular / totalCabeçasLegitima;
            } else if (heirProps?.vinculoDC === 'Descendente') {
                theoreticalEntitlement = legitimaFromComum / numDescendentesNaClasse + legitimaFromParticular / totalCabeçasLegitima;
            } else {
                theoreticalEntitlement = legitimaValor * Q;
            }
        } else {
            theoreticalEntitlement = legitimaValor * Q;
        }

        const netEntitlement = Math.max(0, theoreticalEntitlement - doacao);
        getOrCreatePartilha(heirId, quota.herdeiroNome || 'Herdeiro').valorHerancaLiquida = netEntitlement;
    });

    const totalNetPositiveEntitlement = Array.from(partilhasMap.values()).reduce((sum, p) => sum + (p.valorHerancaLiquida || 0), 0);
    // Apenas a fração da herança correspondente à legítima (ou à partilha colateral) é distribuída em bens; o restante é parte disponível (testamentários)
    const fracaoLegitimaNosBens = herancaValorLiquido > 0
        ? (winningClass === 4 ? valorPartilhaColaterais / herancaValorLiquido : legitimaValor / herancaValorLiquido)
        : 1;

    allAssets.forEach(asset => {
        const assetId = Number(asset.id);
        const donatedPercentage = donatedPercentageByAsset.get(assetId) || 0;
        const remainingPercentage = (100 - donatedPercentage) / 100;
        
        // Calcula o valor herdável considerando apenas a parte NÃO doada
        let inheritableAssetValue = getAssetValueDataObito(asset) * remainingPercentage;
        if (conjuge && isDeCujusAssetCommon(asset, decujus) === 'comum') {
            inheritableAssetValue *= 0.5;
        }

        if (inheritableAssetValue > 0) {
            const category = assetCategoryMap.get(assetId) || 'outros_bens';
            const categoryLabel = ASSET_CATEGORY_LABELS[category] || 'Outro Bem';
            const valorLegitimaDoBem = inheritableAssetValue * fracaoLegitimaNosBens;
            
            partilhasMap.forEach(partilha => {
                const netEntitlement = partilha.valorHerancaLiquida || 0;
                if (netEntitlement > 0 && totalNetPositiveEntitlement > 0) {
                    const proportion = netEntitlement / totalNetPositiveEntitlement;
                    const inheritedValueFromAsset = valorLegitimaDoBem * proportion;
                    if (inheritedValueFromAsset > 0) {
                        partilha.assetShares!.push({ 
                            assetId: asset.id, 
                            assetDescription: asset.descricao || '', 
                            value: inheritedValueFromAsset, 
                            type: 'Herança',
                            category,
                            categoryLabel
                        });
                    }
                }
            });
        }
    });

    const monteColacionavelTotal = herancaValorLiquido + totalCollationValue;
    let disponivelValorCalculado = valorTestamentarioTotal;

    // Legados: bens ou quantias deixados por testamento a legatários (saem da parte disponível)
    let valorLegadoTotal = 0;
    (legados || []).forEach((leg: any) => {
        let valorLegado = 0;
        const legateeId = leg.pessoa_inventario_id != null ? String(leg.pessoa_inventario_id) : '';
        const legatee = familyMap.get(legateeId);
        if (!legatee) return;
        if (leg.tipo_legado === 'quantia') {
            valorLegado = Number(leg.valor) || 0;
        } else if (leg.tipo_legado === 'bem' && leg.bem_relacionado) {
            const parts = String(leg.bem_relacionado).split(':');
            const assetId = parts.length > 1 ? parseInt(parts[1], 10) : NaN;
            if (!isNaN(assetId)) {
                const asset = assetMap.get(assetId);
                if (asset) {
                    const donatedPct = donatedPercentageByAsset.get(assetId) || 0;
                    const remainingPct = (100 - donatedPct) / 100;
                    let valorBem = getAssetValueDataObito(asset) * remainingPct;
                    if (conjuge && isDeCujusAssetCommon(asset, decujus) === 'comum') valorBem *= 0.5;
                    valorLegado = valorBem;
                }
            }
        }
        if (valorLegado > 0) {
            valorLegadoTotal += valorLegado;
            const category = leg.tipo_legado === 'bem' && leg.bem_relacionado ? (assetCategoryMap.get(parseInt(String(leg.bem_relacionado).split(':')[1], 10)) || 'outros_bens') : 'legados';
            getOrCreatePartilha(legateeId, legatee.nome || 'Legatário').assetShares!.push({
                assetId: leg.tipo_legado === 'bem' && leg.bem_relacionado ? parseInt(String(leg.bem_relacionado).split(':')[1], 10) : leg.id,
                assetDescription: leg.descricao || (leg.tipo_legado === 'quantia' ? `Quantia (legado)` : 'Bem (legado)'),
                value: valorLegado,
                type: 'Legado',
                category,
                categoryLabel: ASSET_CATEGORY_LABELS[category] || 'Legado'
            });
        }
    });

    const parteDisponivelParaTestamentarios = Math.max(0, valorTestamentarioTotal - valorLegadoTotal);
    const quotaTotalTestamentaria = (herdeiros_testamentarios || []).reduce((sum: number, ht: any) => {
        const q = typeof ht.quota_testamentaria === 'number' ? ht.quota_testamentaria : parseFloat(String(ht.quota_testamentaria || 0)) || 0;
        return sum + Math.min(100, Math.max(0, q));
    }, 0);
    // Escala para não ultrapassar a parte disponível (máx. 50% da herança quando há herdeiros necessários)
    const escalaQuotaTestamentaria = quotaTotalTestamentaria > 0 && valorTestamentarioTotal > 0
        ? parteDisponivelParaTestamentarios / (herancaValorLiquido * (quotaTotalTestamentaria / 100))
        : 1;
    const escalaQuota = Math.min(1, escalaQuotaTestamentaria);
    (herdeiros_testamentarios || []).forEach((ht: any) => {
        const heirId = ht.pessoa_inventario_id != null ? String(ht.pessoa_inventario_id) : '';
        const person = familyMap.get(heirId);
        if (!person) return;
        const quota = typeof ht.quota_testamentaria === 'number' ? ht.quota_testamentaria : parseFloat(String(ht.quota_testamentaria || 0)) || 0;
        const quotaPct = Math.min(100, Math.max(0, quota)) / 100;
        const valorParteDisponivel = herancaValorLiquido * quotaPct * escalaQuota;
        if (valorParteDisponivel > 0) {
            getOrCreatePartilha(heirId, person.nome || 'Herdeiro testamentário').assetShares!.push({
                assetId: ht.id,
                assetDescription: 'Quota da parte disponível (testamento)',
                value: valorParteDisponivel,
                type: 'Parte disponível',
                category: 'heranca',
                categoryLabel: 'Parte disponível'
            });
        }
    });

    valorLiberalidadePorPessoa.forEach((_, personId) => {
        const person = familyMap.get(personId);
        if (person) getOrCreatePartilha(personId, person.nome || 'Donatário');
    });

    let totalITCMD = 0;
    const partilhasFinais = Array.from(partilhasMap.values());
    const itcmdBaseCmPorHerdeiro = new Map<string, number>();

    partilhasFinais.forEach(p => {
        p.valorMeacao = (p.assetShares || []).filter(s => s.type === 'Meação').reduce((sum, s) => sum + (s.value || 0), 0);
        const valorHerancaFisica = (p.assetShares || []).filter(s => s.type === 'Herança').reduce((sum, s) => sum + (s.value || 0), 0);
        const valorLiberalidadesRecebidas = valorLiberalidadePorPessoa.get(p.herdeiroId?.toString() || '') || 0;
        const valorLegado = (p.assetShares || []).filter(s => s.type === 'Legado').reduce((sum, s) => sum + (s.value || 0), 0);
        const valorParteDisponivel = (p.assetShares || []).filter(s => s.type === 'Parte disponível').reduce((sum, s) => sum + (s.value || 0), 0);

        p.valorHerancaLiquida = valorHerancaFisica + valorParteDisponivel;
        p.valorRecebidoBruto = (p.valorMeacao || 0) + valorHerancaFisica + valorLiberalidadesRecebidas + valorLegado + valorParteDisponivel;

        const baseCausaMortis = valorHerancaFisica + valorLegado + valorParteDisponivel;
        const hid = p.herdeiroId?.toString() || '';
        itcmdBaseCmPorHerdeiro.set(hid, baseCausaMortis);
    });

    partilhasFinais.forEach(p => {
        const hid = p.herdeiroId?.toString() || '';
        const baseCausaMortis = itcmdBaseCmPorHerdeiro.get(hid) || 0;
        const baseCmTributavel = baseCausaMortis;

        const itcmdCausaMortis = ITCMD_USAR_ALIQUOTA_FIXA
            ? baseCmTributavel * itcmdAliquotaFixa
            : calcularITCMDProgressivo(baseCmTributavel);
        const itcmdDoacao = itcmdDoacaoTaxPorPessoa.get(hid) || 0;

        p.itcmdBaseCausaMortis = baseCausaMortis;
        p.itcmdBaseCausaMortisTributavel = baseCmTributavel;
        p.itcmdCausaMortis = itcmdCausaMortis;
        p.itcmdBaseDoacaoBruta = valorLiberalidadePorPessoa.get(hid) || 0;
        p.itcmdDoacao = itcmdDoacao;

        p.itcmdDevido = itcmdCausaMortis + itcmdDoacao;
        totalITCMD += p.itcmdDevido;
        p.valorRecebidoLiquido = p.valorRecebidoBruto - p.itcmdDevido;
    });
    
    const totalPatrimonioBruto = totalPatrimonioBrutoCalc;
    const valorDoacoesExcluidas = valorDoacoesExcluidasCalc;
    // Patrimônio líquido = Ativo - Passivo (Art. 1.847 CC)
    const totalPatrimonioLiquido = totalPatrimonioBruto - totalDividas;

    // Alíquota para exibição: fixa 4% ou progressiva conforme modo
    const aliquotaMedia = ITCMD_USAR_ALIQUOTA_FIXA
        ? itcmdAliquotaFixa
        : (monteColacionavelTotal > 0 ? calcularAliquotaITCMDProgressiva(monteColacionavelTotal) : 0);

    // =======================================================================
    // DETECÇÃO DE VIOLAÇÕES E ALERTAS LEGISLATIVOS
    // =======================================================================
    const violacoesOuAlertasLegislacao: ViolacaoOuAlertaLegislacao[] = [];

    // Verificação: Doação inoficiosa (Art. 1.789, 2.007 e 549 CC)
    // Limite legal: até 50% do monte partível (herança + colação). Valores exibidos devem ser
    // ANTES de aplicar a doação, para que o usuário veja o limite que poderia doar e o excedente.
    const parteDisponivelLimiteLegal = hasHerdeirosNecessarios && monteColacionavelTotal > 0
        ? 0.5 * monteColacionavelTotal
        : monteColacionavelTotal;
    const percentualDisponivelExibicao = hasHerdeirosNecessarios ? 50 : 100;
    if (hasHerdeirosNecessarios && valorDoacoesExcluidas > parteDisponivelLimiteLegal) {
        const valorExcedente = valorDoacoesExcluidas - parteDisponivelLimiteLegal;
        const parteDisponivelFormatada = parteDisponivelLimiteLegal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
        
        violacoesOuAlertasLegislacao.push({
            tipo: 'violacao',
            codigo: 'Art. 1.789, 2.007 e 549 CC',
            titulo: 'Limites Legais (Parte Disponível) - Doação Inoficiosa',
            descricao: `O doador possui herdeiros necessários (filhos, pais, cônjuge). Nessa situação, ele só pode doar até ${percentualDisponivelExibicao}% do monte partível (parte disponível). O valor total das doações (${valorDoacoesExcluidas.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}) ultrapassa a parte disponível (${parteDisponivelFormatada}). A parte que excede (${valorExcedente.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}) caracteriza "doação inoficiosa" e está sujeita à redução, podendo ser anulada conforme Art. 2.007 CC.`,
            valorExcedente,
            percentualDisponivel: percentualDisponivelExibicao,
        });
    }

    // Alertas de atenção (recomendações)
    // 1. Despesas funerárias e do inventário
    const despesasInventario = dividas.filter(d => d.tipo_divida === 'despesa_inventario');
    const totalDespesasInventario = despesasInventario.reduce((sum, d) => sum + (d.saldo_devedor ?? 0), 0);
    
    if (despesasInventario.length === 0) {
        violacoesOuAlertasLegislacao.push({
            tipo: 'atencao',
            codigo: 'Art. 1.847 CC',
            titulo: 'Verificação de Despesas do Inventário',
            descricao: 'O Código Civil determina que as despesas do funeral e outras despesas do inventário devem ser abatidas do patrimônio antes do cálculo da legítima. Verifique se essas despesas estão incluídas nas dívidas do inventário (tipo "Despesa do Inventário").',
        });
    } else {
        violacoesOuAlertasLegislacao.push({
            tipo: 'atencao',
            codigo: 'Art. 1.847 CC',
            titulo: 'Despesas do Inventário Registradas',
            descricao: `Foram registradas ${despesasInventario.length} despesa(s) do inventário no valor total de ${totalDespesasInventario.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}. Essas despesas são deduzidas do patrimônio líquido antes do cálculo da legítima, conforme Art. 1.847 CC.`,
        });
    }

    // 2. Comunhão parcial - título oneroso
    if (decujus.casamento_regime === REGIME_COMUNHAO_PARCIAL) {
        violacoesOuAlertasLegislacao.push({
            tipo: 'atencao',
            codigo: 'Art. 1.660 CC',
            titulo: 'Comunhão Parcial - Título Oneroso',
            descricao: 'No regime de comunhão parcial, apenas os bens adquiridos por título oneroso (compra, permuta, etc.) na constância do casamento entram na comunhão. Bens recebidos por doação ou herança durante o casamento não compõem a meação. Verifique se os bens comuns foram adquiridos por título oneroso.',
        });
    }

    // 3. Doações sem valor declarado
    const doacoesSemValor = (doacoes || []).filter(d => !d.valor || d.valor === 0);
    if (doacoesSemValor.length > 0) {
        violacoesOuAlertasLegislacao.push({
            tipo: 'atencao',
            codigo: 'Art. 2.004, §1º CC',
            titulo: 'Doações sem Valor Declarado no Ato',
            descricao: `Existem ${doacoesSemValor.length} doação(ões) sem valor declarado no ato de liberalidade. Conforme a legislação, o valor de colação deve ser aquele atribuído no ato ou, na ausência, o valor estimativo ao tempo da liberalidade.`,
        });
    }

    if (alertaDoacaoSemDataItcmd) {
        violacoesOuAlertasLegislacao.push({
            tipo: 'atencao',
            codigo: 'ITCMD_DOACAO_SEM_DATA',
            titulo: 'Doações sem data válida para ITCMD',
            descricao:
                'Há pelo menos uma liberalidade sem data da doação válida ou preenchida. O ITCMD de doação em SP considera a soma das transmissões por ano-civil (mesmo doador ao mesmo donatário). Informe a data da doação em todas as liberalidades para agregar corretamente e aplicar a isenção de 2.500 UFESP.',
        });
    }

    obsSucessao.push(
        'ITCMD causa mortis (SP): a Lei 10.705/2000 (art. 6º, I) prevê isenções específicas por tipo de bem (imóvel residencial até 5.000 UFESP, imóvel único até 2.500 UFESP, depósitos até 1.000 UFESP, entre outras). O sistema NÃO aplica teto global no momento; as hipóteses por tipo de bem devem ser conferidas caso a caso até que a parametrização esteja disponível.',
    );
    if (itcmdIsencaoDoacaoValor > 0) {
        obsSucessao.push(
            `ITCMD doação (SP): isenção de ${itcmdIsencaoDoacaoValor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })} (${itcmdSettings.isencaoDoacaoUf} ${ufesp.code}) considerando a SOMA das doações de um mesmo doador ao mesmo donatário no MESMO ano-civil (Lei 10.705/2000 art. 6º, II c/c art. 2º §3º). Ultrapassado o limite no ano, o valor integral somado é tributado (interpretação conservadora SEFAZ/SP).`,
        );
    }

    let calculos: CalculosInventario = {
        totalPatrimonioBruto,
        totalPatrimonioLiquido,
        valorDoacoesExcluidas,
        totalDividas,
        totalDividasCadastradas,
        totalSaldoFinanciamentos,
        financiamentosDetalhe,
        meacaoValor,
        herancaValorBruto: herancaBruta,
        herancaValorLiquido,
        itcmdAliquota: aliquotaMedia, // Alíquota progressiva média para referência
        partilhaPorHerdeiro: partilhasFinais.filter(p => {
            if ((p.valorRecebidoBruto || 0) > 0.01) return true;
            if ((p.itcmdDevido || 0) > 0.01) return true;
            const hid = p.herdeiroId?.toString() || '';
            if (!hid || hid === decujusId) return false;
            const person = familyMap.get(hid);
            return person != null && !person.esta_vivo;
        }),
        winningClass,
        itcmdDevidoTotal: totalITCMD,
        observacoesCalculo: Array.from(new Set(obsSucessao)),
        legitimaValor,
        disponivelValor: disponivelValorCalculado,
        monteColacionavel: monteColacionavelTotal,
        valorLegadoTotal,
        valorLegadoReduzido: valorLegadoTotal,
        tableSetSummary,
        simulationStateCode: stateCode,
        simulationDate,
        fiscalUnitCode: ufesp.code,
        fiscalUnitValueBrl: ufesp.valueBrl,
        termsVersion: TERMS_VERSION,
        violacoesOuAlertasLegislacao,
    };

    const emolSnapshotPolicy = inventory.emolumentos_notas_snapshot_policy ?? 'ESCRITURA_FIRST';

    // Emolumentos de Notas (Fase E)
    // 1) Base do inventário por composição mista:
    //    - Imóveis: valor de escritura/simulação (atualizado)
    //    - Demais bens: referência de óbito
    // 2) Override: base_emol_inventario (quando informado) prevalece.
    const assetsNaoDoados = allAssets.map(asset => {
        const donatedPercentage = donatedPercentageByAsset.get(Number(asset.id)) || 0;
        const remainingPercentage = Math.max(0, (100 - donatedPercentage) / 100);
        return { asset, remainingPercentage };
    });
    const baseImoveisEscrituraBruta = assetsNaoDoados
        .filter(x => 'tipo_imovel' in x.asset)
        .reduce((sum, x) => sum + (getAssetValueDataEscritura(x.asset) * x.remainingPercentage), 0);
    const baseDemaisObitoBruta = assetsNaoDoados
        .filter(x => !('tipo_imovel' in x.asset))
        .reduce((sum, x) => sum + (getAssetValueDataObito(x.asset) * x.remainingPercentage), 0);
    const vEmolInvBruto = baseImoveisEscrituraBruta + baseDemaisObitoBruta;
    const vEmolInvCalculado = Math.max(0, vEmolInvBruto - meacaoValor);
    const baseEmolInventarioDeclarada = Number((inventory as any).base_emol_inventario) || 0;
    const vEmolInv = baseEmolInventarioDeclarada > 0 ? baseEmolInventarioDeclarada : vEmolInvCalculado;

    const emolInv = buildEmolumentosNotasFromCatalog(vEmolInv, 'SP');

    // Emolumentos por doação instrumentada (lookup item I por valor da doação)
    const doacoesComValor = (doacoes || []).filter(d => (Number(d?.valor) || 0) > 0);
    const emolDoacoesTotal = doacoesComValor.reduce((sum, d) => {
        const vDoacao = Number(d.valor) || 0;
        return sum + buildEmolumentosNotasFromCatalog(vDoacao, 'SP').emolumentosTotal;
    }, 0);

    calculos.emolumentosBaseImoveisEscritura = baseImoveisEscrituraBruta;
    calculos.emolumentosBaseDemaisObito = baseDemaisObitoBruta;
    calculos.emolumentosBaseInventario = vEmolInv;
    calculos.emolumentosInventarioTotal = emolInv.emolumentosTotal;
    calculos.emolumentosDoacoesTotal = emolDoacoesTotal;
    calculos.emolumentosDoacoesQtd = doacoesComValor.length;

    calculos.emolumentosFaixa = emolInv.emolumentosFaixa;
    calculos.emolumentosFaixaValorInicial = emolInv.emolumentosFaixaValorInicial;
    calculos.emolumentosFaixaValorFinal = emolInv.emolumentosFaixaValorFinal;
    calculos.emolumentosTotalSemIss = emolInv.emolumentosTotalSemIss + emolDoacoesTotal;
    calculos.emolumentosTabeliao = emolInv.emolumentosTabeliao;
    calculos.emolumentosIss = emolInv.emolumentosIss;
    calculos.emolumentosTotal = emolInv.emolumentosTotal + emolDoacoesTotal;
    calculos.emolumentosPiorCasoTotal = emolInv.emolumentosPiorCasoTotal;
    calculos.emolumentosEconomiaVsPiorCaso = emolInv.emolumentosEconomiaVsPiorCaso;

    if (emolSnapshotPolicy === 'ESCRITURA_FIRST') {
        const faltaVenalAtualizado = assetsNaoDoados.some(
            x =>
                'tipo_imovel' in x.asset &&
                x.remainingPercentage > 0.001 &&
                !imovelTemVenalAtualizadoDeclarado(x.asset),
        );
        if (faltaVenalAtualizado) {
            const violList = [...(calculos.violacoesOuAlertasLegislacao || [])];
            violList.push({
                tipo: 'atencao',
                codigo: 'ESTIMATIVA_SEM_VALOR_ESCRITURA',
                titulo: 'Estimativa de emolumentos sem valor venal atualizado',
                descricao:
                    'Há imóveis incluídos na base sem valor venal atualizado informado para escritura/simulação. O sistema utilizou o valor de referência na data do óbito como substituto. Confira antes de emitir instrumentos.',
            });
            calculos.violacoesOuAlertasLegislacao = violList;
        }
    }

    // Registro de Imóveis (Fase F)
    // Ato principal de inventário/partilha: base transmitida de imóveis (valor atualizado, sem meação e sem parte já doada).
    const imoveis = inventory.imoveis || [];
    const participacoes = inventory.participacoes_societarias || [];
    const registroImoveisBasesPorImovel: Array<{ imovelId: number; descricao?: string; baseInventarioBrl: number }> = [];
    const baseRegistroInventario = imoveis.reduce((sum, imovel) => {
        const donatedPercentage = donatedPercentageByAsset.get(Number(imovel.id)) || 0;
        const remainingPercentage = Math.max(0, (100 - donatedPercentage) / 100);
        let valor = getAssetValueDataEscritura(imovel as AnyAsset) * remainingPercentage;
        if (conjuge && isDeCujusAssetCommon(imovel as AnyAsset, decujus) === 'comum') {
            valor *= 0.5;
        }
        const linhaBase = Math.max(0, valor);
        registroImoveisBasesPorImovel.push({
            imovelId: Number(imovel.id),
            descricao: imovel.descricao ?? undefined,
            baseInventarioBrl: linhaBase,
        });
        return sum + linhaBase;
    }, 0);
    // Emolumentos de RI são calculados ato a ato (matrícula a matrícula), não por base global consolidada.
    const riInventarioTotal = registroImoveisBasesPorImovel.reduce((sum, linha) => {
        const emol = lookupRiRegistro(linha.baseInventarioBrl, 'SP')?.totalBrl || 0;
        return sum + emol;
    }, 0);

    // Desdobro nua/usufruto quando houver doação de imóvel com usufruto.
    let riUsufrutoTotal = 0;
    let prenotacoesQtd = registroImoveisBasesPorImovel.filter(x => x.baseInventarioBrl > 0.01).length;
    (doacoes || []).forEach((doacao) => {
        if (doacao?.status_doacao !== 'doado_com_usufruto') return;
        if (!doacao?.bem_relacionado) return;
        const bemRef = String(doacao.bem_relacionado);
        let baseDoacao = 0;
        if (bemRef.startsWith('imovel:')) {
            const imovelId = parseInt(String(doacao.bem_relacionado).split(':')[1], 10);
            const imovel = imoveis.find(i => Number(i.id) === imovelId);
            baseDoacao = Number(doacao.valor) > 0
                ? Number(doacao.valor)
                : imovel
                    ? getAssetValueDataEscritura(imovel as AnyAsset)
                    : 0;
        } else if (bemRef.startsWith('participacoes_societarias:')) {
            const psId = parseInt(bemRef.split(':')[1], 10);
            const ps = participacoes.find(p => Number(p.id) === psId);
            baseDoacao = Number(doacao.valor) > 0
                ? Number(doacao.valor)
                : ps
                    ? getAssetValueDataEscritura(ps as AnyAsset)
                    : 0;
        } else return;
        if (baseDoacao <= 0) return;
        const valorNua = baseDoacao * (2 / 3);
        const valorUsufruto = baseDoacao * (1 / 3);
        const riNua = lookupRiRegistro(valorNua, 'SP')?.totalBrl || 0;
        const riUsu = lookupRiRegistro(valorUsufruto, 'SP')?.totalBrl || 0;
        if (riNua > 0 || riUsu > 0) {
            riUsufrutoTotal += riNua + riUsu;
            // Regra operacional mínima: dois registros distintos implicam duas pré-notações.
            prenotacoesQtd += 2;
        }
    });
    const prenotacaoUnit = getPrenotacaoValor('SP');
    const prenotacaoTotal = prenotacoesQtd * prenotacaoUnit;
    const riTotal = riInventarioTotal + riUsufrutoTotal + prenotacaoTotal;

    calculos.registroImoveisBaseInventario = baseRegistroInventario;
    calculos.registroImoveisInventarioTotal = riInventarioTotal;
    calculos.registroImoveisUsufrutoTotal = riUsufrutoTotal;
    calculos.registroImoveisPrenotacaoUnitario = prenotacaoUnit;
    calculos.registroImoveisPrenotacoesQtd = prenotacoesQtd;
    calculos.registroImoveisPrenotacaoTotal = prenotacaoTotal;
    calculos.registroImoveisTotal = riTotal;
    calculos.registroImoveisBasesPorImovel = registroImoveisBasesPorImovel.filter(x => x.baseInventarioBrl > 0.01);

    // Distribuição dos emolumentos por herdeiro, proporcional ao valor bruto recebido
    const totalBrutoParaRateio = partilhasFinais.reduce((sum, p) => sum + (p.valorRecebidoBruto || 0), 0);
    const emolTotalParaRateio = calculos.emolumentosTotal || 0;
    if (totalBrutoParaRateio > 0 && emolTotalParaRateio > 0) {
        const fator = emolTotalParaRateio / totalBrutoParaRateio;
        partilhasFinais.forEach(p => {
            const bruto = p.valorRecebidoBruto || 0;
            p.emolumentosDevidos = Number((bruto * fator).toFixed(2));
        });
    } else {
        partilhasFinais.forEach(p => {
            p.emolumentosDevidos = 0;
        });
    }

    return calculos;
}

